<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import * as yup from 'yup';
import type { AxiosError } from 'axios';
import { shiftImportsApi, type ShiftImport } from '@/api/shift-imports';
import { MONTH_NAMES } from '@/utils/month-names';

// 30 MB in bytes, matching the ShiftImportUploader server-side limit.
const MAX_FILE_SIZE = 31_457_280;
const XLSX_EXTENSION = /\.xlsx$/i;

const { t } = useI18n();

// Optional period prefill, set by the Rails view when the user arrives from a
// warned/failed import's "upload corrected file" link. Named prefill* so they
// don't collide with the month/year form field refs below (vue/no-dupe-keys).
interface Props {
  prefillMonth?: number;
  prefillYear?: number;
}
const props = defineProps<Props>();

const MONTHS = MONTH_NAMES.map((title, index) => ({ title, value: index + 1 }));

const now = new Date();
// Planillas are uploaded ahead of time, so the form defaults to the upcoming
// month (rolling into January of next year in December).
const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
// A prefilled year may predate the current one (an older import) — keep it selectable.
const YEARS = [...new Set([props.prefillYear, now.getFullYear(), now.getFullYear() + 1])]
  .filter((year): year is number => !!year)
  .sort((a, b) => a - b);

interface ShiftImportFormValues {
  month: number;
  year: number;
  document?: File | File[] | null;
}

// Vuetify 3's v-file-input models a File[] (plain Array); older callers may hand
// a single File or a FileList. Normalize all three shapes.
function firstFile(value: unknown): File | undefined {
  if (value instanceof File) return value;
  if (Array.isArray(value)) return value[0] as File | undefined;
  if (value instanceof FileList) return value[0];

  return undefined;
}

const validationSchema = yup.object({
  month: yup.number().required(),
  year: yup.number().required(),
  document: yup.mixed()
    .required(t('shiftImport.fileRequired'))
    .test('is-xlsx', t('shiftImport.fileType'), value => XLSX_EXTENSION.test(firstFile(value)?.name ?? ''))
    .test('is-valid-size', t('shiftImport.fileSizeLimit'), value => (firstFile(value)?.size ?? 0) <= MAX_FILE_SIZE),
});

const { handleSubmit, errors, defineField } = useForm<ShiftImportFormValues>({
  validationSchema,
  initialValues: {
    month: props.prefillMonth ?? nextMonth.getMonth() + 1,
    year: props.prefillYear ?? nextMonth.getFullYear(),
  },
});

const [month, monthAttrs] = defineField('month');
const [year, yearAttrs] = defineField('year');
const [document, documentAttrs] = defineField('document');

const asFormData = computed(() => {
  const formData = new FormData();
  const file = firstFile(document.value);
  if (file) formData.append('shift_import[document]', file);
  formData.append('shift_import[month]', String(month.value));
  formData.append('shift_import[year]', String(year.value));

  return formData;
});

// The API answers a rejected upload with a specific Spanish reason — most often
// "Ya hay una importación en curso…", which is actionable in a way the generic
// copy is not. Only fall back when the response carries no errors at all.
interface ShiftImportErrorBody {
  errors?: string[];
}

const {
  mutate: createImport, isError, error, isPending,
} = useMutation<ShiftImport, AxiosError<ShiftImportErrorBody>>({
  mutationFn: () => shiftImportsApi.create(asFormData.value),
  onSuccess: (shiftImport) => {
    window.location.href = `/shift_imports/${shiftImport.id}`;
  },
});

const errorMessage = computed(() => {
  const apiErrors = error.value?.response?.data?.errors;

  return apiErrors?.length ? apiErrors.join(' ') : t('shiftImport.genericError');
});

const onSubmit = handleSubmit(() => createImport());
</script>

<template>
  <v-container class="v-col-12 v-col-md-8">
    <v-card class="pa-4">
      <v-card-title>{{ t('shiftImport.title') }}</v-card-title>
      <v-card-subtitle class="text-wrap">
        {{ t('shiftImport.subtitle') }}
      </v-card-subtitle>

      <v-form
        class="mt-4"
        @submit.prevent="onSubmit"
      >
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <v-select
              v-model="month"
              v-bind="monthAttrs"
              variant="outlined"
              :items="MONTHS"
              :label="t('shiftImport.month')"
              :error-messages="errors.month"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <v-select
              v-model="year"
              v-bind="yearAttrs"
              variant="outlined"
              :items="YEARS"
              :label="t('shiftImport.year')"
              :error-messages="errors.year"
            />
          </v-col>
        </v-row>

        <v-file-input
          v-model="document"
          v-bind="documentAttrs"
          variant="outlined"
          accept=".xlsx"
          prepend-icon="mdi-file-excel"
          :label="t('shiftImport.file')"
          :error-messages="errors.document"
        />

        <v-row class="ma-0">
          <v-spacer />
          <v-btn
            color="primary"
            type="submit"
            :loading="isPending"
          >
            {{ t('shiftImport.submit') }}
          </v-btn>
        </v-row>
      </v-form>

      <v-alert
        v-if="isError"
        class="mt-4"
        type="error"
      >
        {{ errorMessage }}
      </v-alert>
    </v-card>
  </v-container>
</template>
